import { useEffect } from 'react';
import {
  clearFeedbackPageContext,
  publishFeedbackPageContext,
  type FeedbackPageContext,
} from '../providers/feedbackPageContext';

/**
 * Publish this page's screen state for any feedback report filed while it
 * is open. One line per page:
 *
 *     useFeedbackPageContext({ page: 'RunView', runId, activeTab });
 *
 * Keep the object small and human-readable — it is read by whoever
 * triages the report, not by code. Ids, the active tab, which channels
 * are plotted: yes. Whole run payloads: no.
 *
 * The value is compared by JSON, so an object literal rebuilt on every
 * render is fine and callers do not need their own `useMemo`.
 */
export function useFeedbackPageContext(context: FeedbackPageContext): void {
  const serialised = JSON.stringify(context ?? {});

  // The cleanup closes over the same `value` it published — React always
  // runs it before the next effect instance, so no ref is needed.
  useEffect(() => {
    const value = JSON.parse(serialised) as FeedbackPageContext;
    publishFeedbackPageContext(value);
    return () => clearFeedbackPageContext(value);
  }, [serialised]);
}
